// Route Error Page - shown by React Router when a page component throws.
// Replaces the router's default developer screen ("Unexpected Application
// Error! ... Hey developer") that surfaced on prod 2026-09-08.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useRouteError } from 'react-router-dom';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { ThemeButton, ThemeMainWrapper } from '../../pages/Styles/Theme';
import MobHeader from '../Header/MobHeader';

/**
 * RouteErrorPage - errorElement for the router.
 * - Rendered inside AppLayout (sidebar stays) for crashes in a page.
 * - `standalone` renders full-viewport for crashes outside the layout.
 * Reports the problem and offers Reload only. It never navigates the user
 * somewhere else (user direction 2026-09-08): the sidebar is still there
 * for that when the crash is inside the app.
 * The raw error goes to the console only: a client should not read
 * "Cannot read properties of undefined" on screen.
 * Before showing the page it reloads automatically up to RETRY_LIMIT times,
 * SILENTLY - nothing is rendered while retrying (user 2026-09-10: "don't
 * tell the user we are retrying, just refresh"): a transient failure (stale
 * chunk after a deploy, a request that timed out) heals itself; a real bug
 * reaches the page after the third attempt. The attempt counter lives in
 * sessionStorage per pathname and expires after RETRY_WINDOW_MS.
 * Styled with the CrowdVox theme (Montserrat, house blue, ThemeButton) so
 * it matches the rest of the portal rather than Ant Design defaults.
 * Inside the app it renders in ThemeMainWrapper, the same bordered page
 * frame every page uses, so the frame edge next to the sidebar stays
 * exactly where it is on a normal page (user 2026-09-10).
 */
const Wrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: ${({ $standalone }) => ($standalone ? '100vh' : 'calc(100vh - 74px)')};
  padding: 20px;
  background: ${({ $standalone, theme }) => ($standalone ? theme.settings.bodyBg : 'transparent')};
  font-family: ${({ theme }) => theme.fonts.inter};
`;

// Below 992px the desktop sidebar is hidden and every page draws MobHeader
// (fixed, 60px) itself, so the error page must too or the user has no menu
// (user 2026-09-10). MobHeader renders display:none on desktop.
const MobileOffset = styled.div`
  @media (max-width: 991.99px) {
    margin-top: 68px;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 460px;
`;

const IconRing = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(46, 69, 218, 0.08);
  color: ${({ theme }) => theme.settings.mainColor};
  font-size: 30px;
  margin-bottom: 22px;
`;

const Title = styled.h2`
  margin: 0 0 8px;
  font-family: ${({ theme }) => theme.fonts.inter};
  font-size: 22px;
  font-weight: 600;
  line-height: 30px;
  color: ${({ theme }) => theme.settings.primary};
`;

const Sub = styled.p`
  margin: 0 0 24px;
  font-family: ${({ theme }) => theme.fonts.inter};
  font-size: 14px;
  line-height: 22px;
  color: ${({ theme }) => theme.settings.textColor};
`;

const RETRY_LIMIT = 3;
const RETRY_WINDOW_MS = 2 * 60 * 1000;

const retryKey = () => `crowdvox:route-error-retries:${window.location.pathname}`;
// One id per page load, so a mount that runs twice (React StrictMode in dev,
// or two error boundaries catching in the same load) counts one attempt.
const LOAD_ID = (typeof performance !== 'undefined' && performance.timeOrigin) || Date.now();

const readRetries = () => {
  try {
    const raw = sessionStorage.getItem(retryKey());
    if (!raw) return { n: 0, load: null };
    const { n, t, load } = JSON.parse(raw);
    if (Date.now() - t >= RETRY_WINDOW_MS) return { n: 0, load: null };
    return { n: Number(n) || 0, load: load || null };
  } catch (e) {
    return { n: 0, load: null };
  }
};

// Returns true when the write succeeded. A failed write (private mode,
// quota, sandboxed frame) must NOT auto-reload: with no counter the loop
// would never end, so the caller shows the page instead.
const writeRetries = (n) => {
  try {
    sessionStorage.setItem(retryKey(), JSON.stringify({ n, t: Date.now(), load: LOAD_ID }));
    return true;
  } catch (e) {
    return false;
  }
};

const RouteErrorPageInner = ({ standalone = false }) => {
  const error = useRouteError();
  // Decide once, on mount: retry silently or show the page.
  const [retrying] = useState(() => {
    if (typeof window === 'undefined') return false;
    const { n, load } = readRetries();
    if (load === LOAD_ID) {
      // Already decided for this page load (second mount of the same load).
      return n < RETRY_LIMIT; // n was already incremented for this load
    }
    if (n >= RETRY_LIMIT) {
      writeRetries(RETRY_LIMIT); // manual Reload from the page must not restart the auto loop
      return false;
    }
    return writeRetries(n + 1);
  });

  useEffect(() => {
    if (retrying) window.location.reload();
  }, [retrying]);

  // The stack goes to the console for whoever is debugging. In an effect,
  // not the render body, so window resizes / StrictMode do not re-log it.
  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error('[RouteErrorPage]', error);
    }
  }, [error]);

  if (retrying) {
    // Silent: the reload is already on its way, show nothing in between.
    return null;
  }

  const body = (
    <Wrap $standalone={standalone}>
      <Card>
        <IconRing>
          <WarningOutlined />
        </IconRing>
        <Title>We hit a snag</Title>
        <Sub>This page didn&apos;t load the way it should. A quick reload usually fixes it.</Sub>
        <ThemeButton
          icon={<ReloadOutlined />}
          onClick={() => {
            // Pin at click time too: an error page left open past the
            // retry window must not restart the silent loop on Reload.
            writeRetries(RETRY_LIMIT);
            window.location.reload();
          }}
        >
          Reload page
        </ThemeButton>
      </Card>
    </Wrap>
  );
  if (standalone) return body;
  return (
    <>
      <MobHeader />
      <MobileOffset>
        <ThemeMainWrapper>{body}</ThemeMainWrapper>
      </MobileOffset>
    </>
  );
};

// Remount per pathname: React Router clears its boundary on navigation and,
// if the next route throws in the same render pass, updates this element in
// place instead of remounting it, which would skip the retry decision for
// the new page.
const RouteErrorPage = (props) => {
  const { pathname } = useLocation();
  return <RouteErrorPageInner key={pathname} {...props} />;
};

export default RouteErrorPage;
